import { useState } from "react";

import { signOut } from "../auth";
import { HelpSupport } from "../components/HelpSupport";
import { api, type AccountDeletionTombstone } from "../services/api";

/**
 * Settings page.
 *
 * Notification preferences are not part of v1, so this page no longer
 * carries those controls (the bell itself shows for everyone since #191).
 * It now holds:
 *   - Help & Support: raise and track support tickets (the old "My Feedback").
 *   - Privacy and your data: export (JSON download) and account deletion
 *     (typed confirmation, destructive).
 */
export function MePreferences() {
  // Privacy / data actions.
  const [exporting, setExporting] = useState(false);
  const [privacyError, setPrivacyError] = useState<string | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [deletion, setDeletion] = useState<AccountDeletionTombstone | null>(null);

  async function handleExport() {
    setExporting(true);
    setPrivacyError(null);
    try {
      const payload = await api.account.export();
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `my-data-export-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setPrivacyError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }

  async function handleDeleteAccount() {
    setDeleting(true);
    setPrivacyError(null);
    try {
      const result = await api.account.deleteAccount(null);
      setDeletion(result);
      // Give the user a moment to read the confirmation, then sign out so the
      // tombstoned session cannot keep making authenticated calls.
      window.setTimeout(() => void signOut(), 4000);
    } catch (err) {
      setPrivacyError(err instanceof Error ? err.message : "Deletion failed");
      setDeleting(false);
    }
  }

  // Post-deletion takeover: the account is tombstoned, so suppress the editable
  // surface and show the named state + effective timestamp explicitly.
  if (deletion) {
    return (
      <>
        <div className="page-header">
          <h1 className="page-title">Account deletion requested</h1>
        </div>
        <div className="card" style={{ padding: 24 }}>
          <h2 className="card-title">Your account is scheduled for deletion</h2>
          <p className="card-desc" style={{ marginTop: 8 }}>
            Status: {deletion.status}.
            {deletion.deletion_requested_at && (
              <>
                {" "}
                Requested at{" "}
                {new Date(deletion.deletion_requested_at).toLocaleString()}.
              </>
            )}
            {deletion.effective_at && (
              <>
                {" "}
                Anonymisation takes effect{" "}
                {new Date(deletion.effective_at).toLocaleString()}.
              </>
            )}
          </p>
          <p className="form-hint" style={{ marginTop: 12 }}>
            You will be signed out shortly. Contact iQuest support if this was a
            mistake.
          </p>
        </div>
      </>
    );
  }

  const deleteArmed = deleteConfirm.trim().toUpperCase() === "DELETE";

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
        <p className="page-subtitle">
          Manage your support requests and your account data.
        </p>
      </div>

      <HelpSupport />

      <div className="card" style={{ padding: 24, marginTop: 24 }}>
        <h2 className="card-title">Privacy and your data</h2>
        <p className="card-desc" style={{ marginTop: 4 }}>
          Export a copy of your personal data, or request deletion of your
          account.
        </p>

        <div className="config-row" style={{ marginTop: 16 }}>
          <div className="config-row-text">
            <div className="config-label">Export my data</div>
            <div className="config-desc">
              Download a JSON copy of your profile, preferences, and the records
              we hold against your account.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => void handleExport()}
            disabled={exporting}
          >
            {exporting ? "Preparing…" : "Export my data"}
          </button>
        </div>

        <div className="config-row">
          <div className="config-row-text">
            <div className="config-label">Delete my account</div>
            <div className="config-desc">
              This requests permanent anonymisation of your account. It cannot be
              undone. To confirm, type DELETE in the box, then select Delete my
              account.
            </div>
          </div>
          <div
            className="pref-row-control"
            style={{ alignItems: "center", gap: 8 }}
          >
            <input
              type="text"
              className="form-input"
              style={{ width: 140 }}
              value={deleteConfirm}
              onChange={(e) => setDeleteConfirm(e.target.value)}
              placeholder="Type DELETE"
              aria-label="Type DELETE to confirm account deletion"
              disabled={deleting}
            />
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => void handleDeleteAccount()}
              disabled={!deleteArmed || deleting}
            >
              {deleting ? "Deleting…" : "Delete my account"}
            </button>
          </div>
        </div>

        {privacyError && (
          <div className="form-error" style={{ marginTop: 12 }}>
            {privacyError}
          </div>
        )}
      </div>
    </>
  );
}
